import React, { useState } from "react";
import { SecondaryBlueButton } from "@velaro/velaro-component-lib";
import { ScraperJob, ScrapedPage } from "./types";

/** Returns a meaningful content excerpt — finds first real paragraph, skips nav/link boilerplate */
function smartSnippet(content: string, chars = 400): string {
  if (!content) return "(No content)";
  const lines = content.split("\n");
  const realLines: string[] = [];
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    // Skip markdown link-heavy lines (nav menus, breadcrumbs, link lists)
    const linkCount = (line.match(/\[.*?\]\(.*?\)/g) || []).length;
    if (linkCount > 2) continue;
    // Skip very short lines that are likely nav items or labels (< 40 chars unless it's a heading)
    if (line.length < 40 && !line.startsWith("#")) continue;
    // Skip lines that look like pure breadcrumb trails
    if (/^([^<>\n]+\s*[>›»|]\s*)+[^<>\n]+$/.test(line) && line.length < 120) continue;
    realLines.push(line);
    if (realLines.join(" ").length >= chars) break;
  }
  const excerpt = realLines.join(" ").substring(0, chars).trim();
  if (excerpt) return excerpt + (content.length > chars ? "…" : "");
  // Fallback: just take first chars
  return content.substring(0, chars).trim() + (content.length > chars ? "…" : "");
}

interface ScraperJobResultsModalProps {
  job: ScraperJob;
  results: ScrapedPage[];
  onClose: () => void;
}

export default function ScraperJobResultsModal({
  job,
  results,
  onClose
}: ScraperJobResultsModalProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [jsonViewIndex, setJsonViewIndex] = useState<number | null>(null);
  const [filter, setFilter] = useState<"all" | "success" | "failed" | "thin">("all");

  const THIN_WORDS = 30;
  const isThin = (r: ScrapedPage) => r.success && r.word_count < THIN_WORDS;

  const filteredResults = results.filter(r => {
    if (filter === "all") return true;
    if (filter === "success") return r.success && !isThin(r);
    if (filter === "failed") return !r.success;
    if (filter === "thin") return isThin(r);
    return true;
  });

  const successCount = results.filter(r => r.success && !isThin(r)).length;
  const failedCount = results.filter(r => !r.success).length;
  const thinCount = results.filter(isThin).length;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${job.name.replace(/[^a-z0-9]/gi, "_")}_results.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex-shrink-0">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-semibold">{job.name}</h2>
              <p className="text-sm text-slate-500">
                Job ID: {job.job_id} | Created: {formatDate(job.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadJson}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 border border-slate-200 text-slate-600 hover:bg-slate-200 transition-colors"
                title="Download all results as JSON"
              >
                ↓ Download JSON
              </button>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600 text-2xl"
              >
                &times;
              </button>
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <div className="bg-slate-100 px-3 py-2 rounded">
              <div className="text-sm text-slate-500">Total Pages</div>
              <div className="text-xl font-semibold">{results.length}</div>
            </div>
            <div className="bg-green-50 px-3 py-2 rounded">
              <div className="text-sm text-green-600">Good Content</div>
              <div className="text-xl font-semibold text-green-700">{successCount}</div>
            </div>
            {thinCount > 0 && (
              <div className="bg-amber-50 px-3 py-2 rounded">
                <div className="text-sm text-amber-600">Thin / Blocked</div>
                <div className="text-xl font-semibold text-amber-700">{thinCount}</div>
              </div>
            )}
            <div className="bg-red-50 px-3 py-2 rounded">
              <div className="text-sm text-red-600">Failed</div>
              <div className="text-xl font-semibold text-red-700">{failedCount}</div>
            </div>
          </div>
          {thinCount > 0 && (
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              <strong>{thinCount} page{thinCount !== 1 ? "s" : ""}</strong> returned very little content (under {THIN_WORDS} words) —
              likely cookie walls, login gates, redirect pages, or navigation-only pages.
              These are still saved but will produce low-quality embeddings. Review them in the Thin/Blocked tab.
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1 rounded text-sm ${
                filter === "all"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              All ({results.length})
            </button>
            <button
              onClick={() => setFilter("success")}
              className={`px-3 py-1 rounded text-sm ${
                filter === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Success ({successCount})
            </button>
            <button
              onClick={() => setFilter("failed")}
              className={`px-3 py-1 rounded text-sm ${
                filter === "failed"
                  ? "bg-red-100 text-red-700"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Failed ({failedCount})
            </button>
            {thinCount > 0 && (
              <button
                onClick={() => setFilter("thin")}
                className={`px-3 py-1 rounded text-sm ${
                  filter === "thin"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Thin / Blocked ({thinCount})
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {filteredResults.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No results match the selected filter
            </div>
          ) : (
            <div className="space-y-3">
              {filteredResults.map((result, index) => (
                <div
                  key={result.url}
                  className={`border rounded-lg overflow-hidden ${
                    result.success ? "border-slate-200" : "border-red-200"
                  }`}
                >
                  <div
                    className={`p-3 cursor-pointer hover:bg-slate-50 flex items-center justify-between ${
                      !result.success ? "bg-red-50" : isThin(result) ? "bg-amber-50" : "bg-white"
                    }`}
                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-400">
                          #{result.page_number || index + 1}
                        </span>
                        <span
                          className={`w-2 h-2 rounded-full ${
                            !result.success ? "bg-red-500" : isThin(result) ? "bg-amber-400" : "bg-green-500"
                          }`}
                        />
                        <span className="font-medium text-sm truncate">
                          {result.title || result.url}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-1">
                        {result.url}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                      <div className="text-xs text-slate-500">
                        {result.word_count} words
                      </div>
                      <div className="text-xs px-2 py-1 rounded bg-slate-100">
                        {result.content_type}
                      </div>
                      {isThin(result) && (
                        <div className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700" title="Very little content extracted — may be a cookie wall, login gate, or redirect page">
                          Thin
                        </div>
                      )}
                      {result.is_faq && (
                        <div className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">
                          FAQ
                        </div>
                      )}
                      {result.tables && result.tables.length > 0 && (
                        <div className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                          {result.tables.length} table{result.tables.length > 1 ? "s" : ""}
                        </div>
                      )}
                      {result.form_fields && result.form_fields.length > 0 && (
                        <div className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700">
                          {result.form_fields.length} field{result.form_fields.length > 1 ? "s" : ""}
                        </div>
                      )}
                      <span className="text-slate-400">
                        {expandedIndex === index ? "▼" : "▶"}
                      </span>
                    </div>
                  </div>
                  {expandedIndex === index && (
                    <div className="p-3 border-t bg-slate-50">
                      <div className="grid grid-cols-4 gap-4 mb-3 text-xs">
                        <div>
                          <span className="text-slate-500">Characters:</span>{" "}
                          <span className="font-medium">{result.char_count}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Words:</span>{" "}
                          <span className="font-medium">{result.word_count}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Scraped:</span>{" "}
                          <span className="font-medium">{formatDate(result.scraped_at)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Page:</span>{" "}
                          <span className="font-medium">#{result.page_number || index + 1}</span>
                        </div>
                      </div>

                      {/* Headings */}
                      {result.headings && result.headings.length > 0 && (
                        <div className="mb-3">
                          <div className="text-sm font-medium mb-2">
                            Headings ({result.headings.length})
                          </div>
                          <div className="bg-white p-2 rounded text-sm space-y-1 max-h-32 overflow-y-auto">
                            {result.headings.map((h, i) => (
                              <div
                                key={i}
                                className="text-slate-700"
                                style={{ paddingLeft: `${(h.level - 1) * 16}px` }}
                              >
                                <span className="text-slate-400 font-mono text-xs mr-1">H{h.level}</span>
                                {h.text}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Form Fields */}
                      {result.form_fields && result.form_fields.length > 0 && (
                        <div className="mb-3">
                          <div className="text-sm font-medium mb-2">
                            Form Fields ({result.form_fields.length})
                          </div>
                          <div className="bg-white rounded overflow-hidden">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="text-left p-2 font-medium text-slate-600">Name</th>
                                  <th className="text-left p-2 font-medium text-slate-600">Type</th>
                                  <th className="text-left p-2 font-medium text-slate-600">Input</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {result.form_fields.map((field, i) => (
                                  <tr key={i}>
                                    <td className="p-2 font-mono">{field.name}</td>
                                    <td className="p-2">{field.field_type}</td>
                                    <td className="p-2">{field.type}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Tables */}
                      {result.tables && result.tables.length > 0 && (
                        <div className="mb-3">
                          <div className="text-sm font-medium mb-2">
                            Tables ({result.tables.length})
                          </div>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {result.tables.map((table, ti) => (
                              <div key={ti} className="bg-white rounded overflow-x-auto">
                                <table className="w-full text-xs border">
                                  <tbody>
                                    {table.slice(0, 10).map((row, ri) => (
                                      <tr key={ri} className={ri === 0 ? "bg-slate-100 font-medium" : "border-t"}>
                                        {row.map((cell, ci) => (
                                          <td key={ci} className="p-1.5 border-r last:border-r-0">
                                            {cell}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                    {table.length > 10 && (
                                      <tr>
                                        <td colSpan={table[0]?.length || 1} className="p-1.5 text-center text-slate-500">
                                          + {table.length - 10} more rows
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* FAQ Pairs */}
                      {result.is_faq && result.faq_pairs && result.faq_pairs.length > 0 && (
                        <div className="mb-3">
                          <div className="text-sm font-medium mb-2">
                            FAQ Pairs ({result.faq_pairs.length})
                          </div>
                          <div className="space-y-2">
                            {result.faq_pairs.slice(0, 3).map((pair, i) => (
                              <div key={i} className="bg-white p-2 rounded text-sm">
                                <div className="font-medium text-blue-700">Q: {pair.question}</div>
                                <div className="text-slate-600 mt-1">A: {pair.answer.length > 150 ? pair.answer.substring(0, 150) + "…" : pair.answer}</div>
                              </div>
                            ))}
                            {result.faq_pairs.length > 3 && (
                              <div className="text-xs text-slate-500">
                                + {result.faq_pairs.length - 3} more pairs
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Content Preview / Raw JSON toggle */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-medium">
                              {jsonViewIndex === index ? "Raw JSON" : "Content Snippet"}
                            </div>
                            <button
                              onClick={() => setJsonViewIndex(jsonViewIndex === index ? null : index)}
                              className="text-xs px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-100"
                            >
                              {jsonViewIndex === index ? "← Snippet" : "{ } Raw JSON"}
                            </button>
                          </div>
                          <span className="text-xs text-slate-400">~{Math.round((result.content || "").length / 5)} words extracted</span>
                        </div>
                        {jsonViewIndex === index ? (
                          <pre className="bg-slate-900 text-green-400 p-3 rounded text-xs max-h-96 overflow-y-auto overflow-x-auto font-mono leading-relaxed">
                            {JSON.stringify(
                              { ...result, content: result.content ? result.content.substring(0, 2000) + (result.content.length > 2000 ? `\n… (${result.content.length - 2000} more chars)` : "") : "" },
                              null,
                              2
                            )}
                          </pre>
                        ) : (
                          <div className="bg-white p-3 rounded text-sm max-h-48 overflow-y-auto whitespace-pre-wrap text-slate-700 leading-relaxed border border-slate-100">
                            {smartSnippet(result.content || result.content_preview || "")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-4 border-t flex-shrink-0 flex justify-end">
          <SecondaryBlueButton label="Close" onClick={onClose} />
        </div>
      </div>
    </div>
  );
}
